import { fetchCountries } from 'actions/entities/countries/countries';
import { fetchOffer } from 'actions/entities/offers/fetchOffer/fetchOffer';
import { fetchOffers } from 'actions/entities/offers/fetchOffers/fetchOffers';
import { fetchIPCountryCode } from 'actions/geolocation/geolocation';
import { fetchPublicKey } from 'actions/publicKey/publicKey';
import {
  trackSelectInstalmentPlansTab,
  trackSelectSinglePaymentsTab,
} from 'actions/tracker/tracker';
import { setDisplayCoupon, setFieldValue } from 'actions/ui/ui';
import { Info } from 'components/Alert/Info/Info';
import { FetchOffersError } from 'components/FetchOffersError';
import { PausedError } from 'components/PausedError/PausedError';
import { CountrySelector } from 'components/PaymentOptions/components/CountrySelector/CountrySelector';
import { NoOptionsError } from 'components/PaymentOptions/components/NoOptionsError/NoOptionsError';
import { RecurringPaymentOptions } from 'components/PaymentOptions/RecurringPaymentOptions/RecurringPaymentOptions';
import { SinglePaymentOptions } from 'components/PaymentOptions/SinglePaymentOptions/SinglePaymentOptions';
import { Heading } from 'components/ui/Heading/Heading';
import { Spinner } from 'components/ui/Spinner';
import { Tabs } from 'components/ui/Tabs/Tabs';
import { PAUSED_ERROR } from 'constants/errors';
import { useIndiaFxTestExperiment } from 'hooks/useIndiaFxTestExperiment/useIndiaFxTestExperiment';
import { SessionType } from 'models';
import { useEffect, useState } from 'react';
import { type ConnectedProps, connect } from 'react-redux';
import type { RootState } from 'reducers/types';
import { getOfferFields } from 'selectors/entities/fields/fields';
import {
  getForeignOffers,
  getLocalOffers,
  getRecurringOffers,
} from 'selectors/entities/offers/offers';
import { getRecipient } from 'selectors/entities/recipient/recipient';
import {
  getCreatePaymentError,
  getCurrentCountry,
  getCurrentLocale,
  getFieldValue,
  getTotalAmount,
  isOfferAstropay,
} from 'selectors/ui/ui';
import { useTranslations } from 'utils';
import './PaymentOptions.scss';

const SINGLE_TAB_INDEX = 0;

type PaymentOptionsProps = ConnectedProps<typeof connector> & {
  onNext: () => void;
};

const PaymentOptionsComponent = ({
  country,
  createPaymentError,
  fetchCountries,
  fetchIPCountryCode,
  fetchOffer,
  fetchOffers,
  fetchPublicKey,
  foreignOptions = [],
  isOfferAstropay = false,
  localOptions = [],
  locale,
  onNext,
  purchase,
  recipient,
  recurringOffers,
  sessionType,
  setDisplayCoupon,
  setFieldValue,
}: PaymentOptionsProps) => {
  const i18n = useTranslations();
  const { hideNonFxOptions } = useIndiaFxTestExperiment();
  const [isFetchingOffer, setIsFetchingOffer] = useState(false);
  const [isFetchingOffers, setIsFetchingOffers] = useState(true);
  const [failedRetrievingOffers, setFailedRetrievingOffers] = useState(false);

  const noOptions =
    localOptions.length === 0 &&
    foreignOptions.length === 0 &&
    recurringOffers.length === 0;
  const hasSingleOptions = !!localOptions.length || !!foreignOptions.length;
  const hasRecurringOptions = !!recurringOffers.length;
  const isRecurringSessionWithoutOptions =
    sessionType === SessionType.Recurring && !hasRecurringOptions;
  const isOneOffSessionWithoutOptions =
    sessionType === SessionType.OneOff && !hasSingleOptions;
  const showTabs = hasSingleOptions && hasRecurringOptions;
  const { Tab, TabList, TabPanel, TabPanels } = Tabs;

  useEffect(() => {
    let didCancel = false;

    async function fetchData() {
      try {
        await Promise.all([
          fetchCountries(locale),
          fetchOffers(true),
          fetchPublicKey(),
          fetchIPCountryCode(),
        ]);
        setFailedRetrievingOffers(false);
      } catch {
        setFailedRetrievingOffers(true);
      }

      if (!didCancel) {
        setIsFetchingOffers(false);
      }
    }

    fetchData();

    return () => {
      didCancel = true;
    };
  }, [fetchCountries, fetchIPCountryCode, fetchOffers, fetchPublicKey, locale]);

  async function setOption(name: string, value: string) {
    setIsFetchingOffer(true);
    await setFieldValue(name, value);
    await fetchOffer(value);
    setIsFetchingOffer(false);
  }

  const handleCountryChange = async (countryCode: string) => {
    setFailedRetrievingOffers(false);
    setIsFetchingOffers(true);
    try {
      await fetchOffers();
    } catch {
      setFailedRetrievingOffers(true);
    }
    setIsFetchingOffers(false);
  };

  if (!country.code) {
    return (
      <>
        <Heading as="h2" size="large" className="font-medium">
          {i18n.t('checkout.paymentOptions.title')}
        </Heading>
        <CountrySelector onChange={handleCountryChange} />
        <Info>
          <Info.FilledIcon height="14" width="14" className="mr-2 -ml-2" />
          <>{i18n.t('checkout.paymentOptions.noCountrySelected')}</>
        </Info>
      </>
    );
  }

  if (createPaymentError === PAUSED_ERROR) {
    return <PausedError />;
  }

  if (failedRetrievingOffers) {
    return <FetchOffersError />;
  }
  if (isFetchingOffers) {
    return <Spinner />;
  }

  if (
    isRecurringSessionWithoutOptions ||
    isOneOffSessionWithoutOptions ||
    noOptions
  ) {
    return (
      <NoOptionsError
        recipientName={recipient.name || ''}
        purchase={{
          value: purchase,
          currency: recipient.currency,
        }}
      />
    );
  }

  const renderSingleOptions = () => (
    <SinglePaymentOptions
      isFetchingOffer={isFetchingOffer}
      isOfferAstropay={isOfferAstropay}
      selectOption={setOption}
      countryCode={country?.code as string}
      onNext={onNext}
      hideNonFxOptions={hideNonFxOptions}
    />
  );

  const renderRecurringOptions = () => (
    <RecurringPaymentOptions
      isFetchingOffer={isFetchingOffer}
      onNext={onNext}
      selectOption={setOption}
    />
  );

  const renderOptions = () => {
    if (sessionType === SessionType.Recurring) {
      return renderRecurringOptions();
    }
    if (sessionType === SessionType.OneOff) {
      return renderSingleOptions();
    }
    if (showTabs) {
      return (
        <Tabs
          defaultActiveIndex={SINGLE_TAB_INDEX}
          className="PaymentOptionsTabs h-full flex flex-col"
          onChange={() => setDisplayCoupon(false)}
        >
          <TabList>
            <Tab
              data-testid="single-payment-offers"
              onSelect={() => trackSelectSinglePaymentsTab()}
            >
              {i18n.t('paymentOptions.singlePayment')}
            </Tab>
            <Tab
              data-testid="recurring-payment-offers"
              onSelect={() => trackSelectInstalmentPlansTab()}
            >
              {i18n.t('paymentOptions.paymentPlan')}
            </Tab>
          </TabList>
          <TabPanels className="flex-1 h-full">
            <TabPanel className="h-full">{renderSingleOptions()}</TabPanel>
            <TabPanel className="h-full">{renderRecurringOptions()}</TabPanel>
          </TabPanels>
        </Tabs>
      );
    }

    if (hasSingleOptions) {
      return renderSingleOptions();
    }

    return renderRecurringOptions();
  };

  return (
    <div className="flex flex-col h-full" data-testid="payment-options">
      <div>
        <Heading as="h2" size="large" className="fontWeight-semiBold">
          {i18n.t('checkout.paymentOptions.title')}
        </Heading>
        <CountrySelector onChange={handleCountryChange} />
      </div>
      <div className="flex-1">{renderOptions()}</div>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  country: getCurrentCountry(state),
  createPaymentError: getCreatePaymentError(state),
  foreignOptions: getForeignOffers(state),
  isOfferAstropay: isOfferAstropay(state),
  localOptions: getLocalOffers(state),
  locale: getCurrentLocale(state),
  offerFields: getOfferFields(state),
  purchase: getTotalAmount(state),
  recipient: getRecipient(state),
  recurringOffers: getRecurringOffers(state),
  sessionType: getFieldValue(state, 'session_type'),
});

const mapDispatchToProps = {
  fetchCountries,
  fetchIPCountryCode,
  fetchOffer,
  fetchOffers,
  fetchPublicKey,
  setFieldValue,
  setDisplayCoupon,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
const PaymentOptions = connector(PaymentOptionsComponent);

export { PaymentOptions };
